import { isEqual, orderBy } from 'lodash'
import axios from 'axios'
import { getUser, getUsers } from '@/api/users'
import { getApps } from '@/api/apps'
import { DISPATCHER_ROLE, getInstance, SUPERADMIN_ROLE } from '@/auth'
import User from '../../models/user'
import App from '../../models/app'
import { errorMutationHandler } from '../utils'

export const LOADING_USERS = 'LOADING_USERS'
export const RECEIVE_USER = 'RECEIVE_USER'
export const RECEIVE_USERS = 'RECEIVE_USERS'
export const SAVING_USER = 'SAVING_USER'
export const CLEAR_USER_ERROR = 'CLEAR_USER_ERROR'
export const ERROR_USERS = 'ERROR_USERS'
export const LOADING_APPS = 'LOADING_APPS'
export const RECEIVE_APPS = 'RECEIVE_APPS'
export const ERROR_APPS = 'ERROR_APPS'

const operatorRole = 'Operator'

const state = () => {
  return {
    all: {},
    userIndex: {},
    loading: false,
    saving: false,
    error: null,
    apps: {},
    appIndex: {},
    appsLoading: false,
    appsError: null,
  }
}

const getters = {
  // Cache base getter for all users to avoid repeated Object.values calls
  _allUsers: (state) => {
    return Object.values(state.all)
  },
  getAllUsers: (state) => state.all,
  getLoading: (state) => state.loading,
  getSaving: (state) => state.saving,
  getError: (state) => state.error,
  getOperatorsForBusinessUnit: (_state, getters) => (businessUnitId) => {
    return getters
      .getUsersForBusinessUnit(businessUnitId)
      .filter((user) => user.roles.includes(operatorRole))
  },
  // We should work to use uuid instead of user_sid when referencing users
  getBySid: (state) => (userSid) => {
    if (!userSid) {
      return null
    }
    const userId = state.userIndex[userSid]
    return state.all[userId] || null
  },
  // Actor lookup only; user selectors must use getBySid.
  getPrincipalBySid: (state, getters) => (userSid) => {
    const user = getters.getBySid(userSid)
    if (user) {
      return user
    }

    return state.apps?.[state.appIndex?.[userSid]] || null
  },
  getSortedListItems: (state, getters) => {
    return orderBy(getters._allUsers, ['isDisabled', 'created'], ['asc', 'desc'])
  },
  getNonDisabledUsers: (state, getters) => {
    return getters._allUsers.filter((user) => user.isDisabled === false)
  },
  getVisibleUsers: (_state, getters, _rootState, rootGetters) => {
    const authService = getInstance()
    const partnerIds = rootGetters['permissions/getPartnerIds']
    const hostIds = rootGetters['permissions/getHostIds']

    const users = getters.getNonDisabledUsers.filter(
      (user) =>
        user.organizationId === authService.user.org_id ||
        partnerIds.includes(user.organizationId) ||
        user.scopes.some((scope) => hostIds.includes(scope)),
    )

    return orderBy(users, [(user) => user.name.toLowerCase()])
  },
  getUsersForOrganizations: (_, getters) => (organizationId) =>
    getters.getNonDisabledUsers.filter((user) => user.organizationId === organizationId),
  getUserInListBySid: (state) => (sid) => state.all[sid] || null,
  getDispatchersOrAdminsInOrganization: (_state, getters) => (businessUnitId) => {
    return getters.getNonDisabledUsers.some(
      (user) =>
        user.organizationId === businessUnitId &&
        (user.roles.includes(DISPATCHER_ROLE) || user.roles.includes(SUPERADMIN_ROLE)),
    )
  },
  getAllUsersForOrganization: (_state, getters) => (organizationId) => {
    const users = getters.getNonDisabledUsers.filter(
      (user) => user.organizationId === organizationId,
    )

    return orderBy(users, [(user) => user.name.toLowerCase()])
  },
  getUsersRelatedToBusinessUnit: (_state, getters, _rootState, rootGetters) => (businessUnitId) => {
    const context = rootGetters['permissions/getContext']
    const scopes = context.roleAssignments.map((ra) => ra.businessUnitId)

    // Short circuit if the users scopes are not loaded yet
    if (scopes.length === 0) return []

    let users = getters.getUsersForBusinessUnit(businessUnitId)

    // If I belong to the business unit, fetch users partnered to said
    // business unit OUTSIDE of my direct lineage
    if (scopes.includes(businessUnitId)) {
      const partnerBusinessUnitIds = context.lineage
        .filter((bu) => bu.id === businessUnitId)
        .map((bu) => [...bu.partnerIds, ...bu.hostIds])
        .flat()

      users = users.concat(getters.getUsersForBusinessUnits(partnerBusinessUnitIds))
      // If I am partnered to the business unit, fetch users partnered to
      // said business unit INSIDE of my direct lineage
    } else {
      const partneredBusinessUnitIds = context.lineage
        .filter((bu) => bu.hostIds.includes(businessUnitId))
        .map((bu) => bu.id)

      users = users.concat(getters.getUsersForBusinessUnits(partneredBusinessUnitIds))
    }

    return orderBy(users, [(user) => user.name.toLowerCase()])
  },
  getUsersForBusinessUnit: (_state, getters) => (businessUnitId) => {
    return getters.getNonDisabledUsers.filter((user) => user.scopes.includes(businessUnitId))
  },
  getUsersForBusinessUnits: (_state, getters) => (businessUnitIds) => {
    return getters.getNonDisabledUsers.filter((user) =>
      businessUnitIds.some((id) => user.scopes.includes(id)),
    )
  },
}

const actions = {
  fetchAllUsers({ commit }) {
    commit(LOADING_USERS)

    return getUsers()
      .then((res) => commit(RECEIVE_USERS, res))
      .catch((err) => commit(ERROR_USERS, err))
  },
  fetchUsersBySid({ commit }, sid) {
    commit(LOADING_USERS)

    return getUser(sid)
      .then((res) => commit(RECEIVE_USER, res))
      .catch((err) => commit(ERROR_USERS, err))
  },
  fetchAllApps({ commit }) {
    commit(LOADING_APPS)

    return getApps()
      .then((res) => commit(RECEIVE_APPS, res))
      .catch((err) => commit(ERROR_APPS, err))
  },
}

const mutations = {
  [LOADING_USERS](state) {
    state.loading = true
  },
  [RECEIVE_USERS](state, { data }) {
    const receivedUsers = {}
    const userIndex = {}

    data.forEach((json) => {
      receivedUsers[json.uuid] = Object.freeze(new User(json))
      userIndex[json.id] = json.uuid
    })

    if (!isEqual(receivedUsers, state.all)) {
      state.all = Object.freeze(receivedUsers)
      state.userIndex = Object.freeze(userIndex)
    }

    state.loading = false
    state.error = null
  },
  [RECEIVE_USER](state, { data }) {
    state.saving = false
    state.loading = false
    const allUsers = { ...state.all, [data.uuid]: Object.freeze(new User(data)) }
    const userIndex = { ...state.userIndex, [data.id]: data.uuid }
    state.all = Object.freeze(allUsers)
    state.userIndex = Object.freeze(userIndex)
    state.error = null
  },
  [ERROR_USERS](state, error) {
    state.saving = false
    state.loading = false
    errorMutationHandler(state, error)
  },
  [LOADING_APPS](state) {
    state.appsLoading = true
  },
  [RECEIVE_APPS](state, { data }) {
    const receivedApps = {}
    const appIndex = {}

    data.forEach((json) => {
      receivedApps[json.uuid] = Object.freeze(new App(json))
      if (appIndex[json.id] === undefined) {
        appIndex[json.id] = json.uuid
      }
    })

    if (!isEqual(receivedApps, state.apps)) {
      state.apps = Object.freeze(receivedApps)
      state.appIndex = Object.freeze(appIndex)
    }

    state.appsLoading = false
    state.appsError = null
  },
  [ERROR_APPS](state, error) {
    state.appsLoading = false

    if (!axios.isCancel(error)) {
      state.appsError = error
    }
  },
  [CLEAR_USER_ERROR](state) {
    state.error = null
  },
  [SAVING_USER](state) {
    state.saving = true
    state.error = null
  },
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
}
